<template>
    <div
        class="filter-chip-container"
        :class="{ 'chip-container--removable': removable, 'chip-container--clearable': clearable }"
        :data-filter-field="dataFilterField"
    >
        <CustomPopover
            ref="customPopover"
            :icon="icon"
            :label="getChipLabel()"
            :show-dropdown-arrow="false"
            :button-class="buttonClass"
            popover-class="modern-custom-popover"
            :show-form-footer="true"
            :removable="removable"
            :clearable="clearable"
            @popover-change="onPopoverChange"
            @remove-click="onRemoveClick"
            @clear-click="onClearClick"
            @clear="onFooterClear"
            @apply="onFooterApply"
        >
            <template slot="content">
                <div class="broadcast-composite-container">
                    <div class="broadcast-composite-field">
                        <label class="broadcast-composite-label">{{ $t("chat.filterChip.operator") }}</label>
                        <select v-model="currentOperator" class="broadcast-composite-select" @change="onOperatorChange">
                            <option v-for="option in operatorOptions" :key="option.value" :value="option.value">
                                {{ option.label }}
                            </option>
                        </select>
                    </div>
                    <div class="broadcast-composite-field">
                        <label class="broadcast-composite-label">{{ filterName || $t("chat.filterChip.select") }}</label>
                        <div class="broadcast-composite-value-wrap">
                            <MinimalPopoverSelect
                                ref="miniPopoverSelect"
                                :key="'mps-' + String(effectiveMultiple)"
                                :value="selectBindValue"
                                :value-text="isEditingPopover ? '' : valueText"
                                :options="options"
                                :multiple="effectiveMultiple"
                                :filter-name="$t('auto.select')"
                                :show-icon="true"
                                :enable-search="enableSearch"
                                :show-dropdown-arrow="true"
                                :button-class="
                                    'select-button broadcast-inline-select-trigger' +
                                    (hasValue ? ' broadcast-inline-select-trigger--has-value' : '')
                                "
                                popover-class="filter-select-popover"
                                check-icon="done"
                                :default-icon="icon"
                                :removable="false"
                                :clearable="false"
                                @input="onInput"
                                @change="onValueChange"
                                @search-input="onSearchInput"
                            />
                        </div>
                    </div>
                </div>
            </template>
        </CustomPopover>
    </div>
</template>

<script>
import CustomPopover from "./CustomPopover.vue";
import MinimalPopoverSelect from "./MinimalPopoverSelect.vue";

export default {
    name: "SelectOperatorCompositeFilterChip",
    components: {
        CustomPopover,
        MinimalPopoverSelect,
    },
    props: {
        value: {
            type: [String, Array, Boolean, Number, Object],
            default: null,
        },
        valueText: {
            type: String,
            default: "",
        },
        options: {
            type: Array,
            default: function () {
                return [];
            },
        },
        filterName: {
            type: String,
            default: "",
        },
        icon: {
            type: String,
            default: "filter_list",
        },
        enableSearch: {
            type: Boolean,
            default: false,
        },
        removable: {
            type: Boolean,
            default: false,
        },
        clearable: {
            type: Boolean,
            default: false,
        },
        buttonClass: {
            type: String,
            default: "",
        },
        hasValue: {
            type: Boolean,
            default: false,
        },
        dataFilterField: {
            type: String,
            default: "",
        },
        operator: {
            type: String,
            default: null,
        },
    },
    data: function () {
        return {
            currentOperator: "contains",
            isEditingPopover: false,
            draftValue: null,
        };
    },
    computed: {
        selectBindValue: function () {
            if (this.isEditingPopover) {
                return this.draftValue;
            }
            return this.value;
        },
        operatorOptions: function () {
            return [
                { label: this.$t("chat.filterChip.contains"), value: "contains" },
                { label: this.$t("chat.filterChip.equals"), value: "equals" },
            ];
        },
        effectiveMultiple: function () {
            return this.currentOperator === "contains";
        },
    },
    watch: {
        operator: function (val) {
            if (!this.isEditingPopover) {
                this.currentOperator = val || "contains";
            }
        },
    },
    mounted: function () {
        if (this.operator) {
            this.currentOperator = this.operator;
        } else {
            this.currentOperator = "contains";
        }
    },
    methods: {
        getChipLabel: function () {
            if (this.valueText) {
                return this.filterName ? `${this.filterName}: ${this.valueText}` : this.valueText;
            }
            if (this.value !== undefined && this.value !== null && this.value !== "") {
                const displayValue =
                    Array.isArray(this.value) && this.value.length > 0 ? this.value : this.value;
                return this.filterName ? `${this.filterName}: ${displayValue}` : displayValue;
            }
            return this.filterName || this.$t("chat.filterChip.select");
        },
        cloneFilterValue: function (v) {
            if (v === undefined) {
                return null;
            }
            if (v === null) {
                return null;
            }
            if (Array.isArray(v)) {
                return v.slice();
            }
            return v;
        },
        normalizeDraftForCommit: function () {
            var val = this.draftValue;
            var op = this.currentOperator;
            if (op === "equals") {
                if (Array.isArray(val) && val.length) {
                    return val[0];
                }
                return val;
            }
            if (op === "contains") {
                if (
                    val !== null &&
                    val !== undefined &&
                    val !== "" &&
                    !Array.isArray(val)
                ) {
                    return [val];
                }
                return val;
            }
            return val;
        },
        onInput: function (val) {
            if (this.isEditingPopover) {
                this.draftValue = val;
            }
        },
        onValueChange: function (val) {
            if (this.isEditingPopover) {
                this.draftValue = val;
            }
        },
        onFooterClear: function () {
            this.currentOperator = "contains";
            this.draftValue = null;
            this.$emit("input", null);
            this.$emit("change", null);
            this.$emit("operator-change", "contains");
        },
        onFooterApply: function () {
            var committed = this.normalizeDraftForCommit();
            this.$emit("input", committed);
            this.$emit("change", committed);
            this.$emit("operator-change", this.currentOperator);
        },
        onOperatorChange: function (event) {
            if (event && event.target) {
                this.currentOperator = event.target.value;
            }
            if (this.currentOperator === "equals") {
                if (Array.isArray(this.draftValue) && this.draftValue.length) {
                    this.draftValue = this.draftValue[0];
                }
            } else if (this.currentOperator === "contains") {
                if (
                    this.draftValue !== null &&
                    this.draftValue !== undefined &&
                    this.draftValue !== "" &&
                    !Array.isArray(this.draftValue)
                ) {
                    this.draftValue = [this.draftValue];
                }
            }
        },
        onPopoverChange: function (visible) {
            if (visible) {
                this.isEditingPopover = true;
                this.draftValue = this.cloneFilterValue(this.value);
                this.currentOperator = this.operator || "contains";
            } else {
                this.isEditingPopover = false;
                this.draftValue = null;
                this.currentOperator = this.operator || "contains";
            }
            this.$emit("popover-change", visible);
        },
        onRemoveClick: function () {
            this.$emit("remove-click");
        },
        onClearClick: function () {
            this.$emit("clear-click");
        },
        onSearchInput: function (query) {
            this.$emit("search-input", query);
        },
        openPopover: function () {
            if (this.$refs.customPopover) {
                this.$refs.customPopover.openPopover();
            }
        },
        closePopover: function () {
            if (this.$refs.customPopover) {
                this.$refs.customPopover.closePopover();
            }
        },
    },
};
</script>

<style lang="scss">
.broadcast-composite-container {
    padding: 16px;
    min-width: 320px;
}

.broadcast-composite-field {
    margin-bottom: 16px;

    &:last-child {
        margin-bottom: 0;
    }
}

.broadcast-composite-label {
    display: block;
    font-size: 12px;
    font-weight: 600;
    color: #374151;
    margin-bottom: 8px;
}

.broadcast-composite-select {
    width: 100%;
    padding: 8px 12px;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    font-size: 13px;
    background: white;
    color: #374151;

    &:focus {
        outline: none;
        border-color: #22272d;
        box-shadow: 0 0 0 2px rgba(34, 39, 45, 0.1);
    }

    option {
        padding: 4px 8px;
    }
}

.broadcast-composite-value-wrap {
    width: 100%;
}

.modern-custom-popover .broadcast-inline-select-trigger.select-button {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 8px 12px;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    font-size: 13px;
    font-weight: 600;
    background: #fff;
    color: #374151;
    min-height: 38px;
    box-sizing: border-box;
    cursor: pointer;
    user-select: none;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;

    &:hover {
        border-color: #22272d;
        background: #fafafa;
    }

    &.broadcast-inline-select-trigger--has-value {
        background: #f9fafb;
        border-color: #d1d5db;
    }

    .button-text-v2 {
        flex: 1;
        text-align: left;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .button-icon {
        color: #6b7785;
        flex-shrink: 0;
    }

    .dropdown-arrow {
        color: #6b7785;
        flex-shrink: 0;
    }
}
</style>
